import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

/** The selected club context for the whole back-office app, or "all" for cross-club. */
export type ClubFilter = string | "all";

interface ClubFilterValue {
  /** The selected club id, or "all" for the cross-club default. */
  clubId: ClubFilter;
  /** True when no single club is selected (the cross-club default). */
  isAll: boolean;
  /** The selected club id as a query argument, or undefined when "all". */
  scopeId: string | undefined;
  setClubId: (clubId: ClubFilter) => void;
}

const ClubFilterContext = createContext<ClubFilterValue | null>(null);

/**
 * The back-office club switcher's selection, lifted out of the topbar so every ops surface
 * (dashboard, requests inbox, all-trips table) scopes to the same club (TW-3440). The switcher
 * writes here and the pages read it — picking a club now filters the whole app, where before it
 * was cosmetic. Defaults to "all" (the cross-club view back-office staff start from). Mounted in
 * BackOfficeLayout only; club users never see a switcher (RLS already scopes them to one club).
 */
export function ClubFilterProvider({ children }: { children: ReactNode }) {
  const [clubId, setClubId] = useState<ClubFilter>("all");
  const value = useMemo<ClubFilterValue>(
    () => ({
      clubId,
      isAll: clubId === "all",
      scopeId: clubId === "all" ? undefined : clubId,
      setClubId,
    }),
    [clubId],
  );
  return <ClubFilterContext.Provider value={value}>{children}</ClubFilterContext.Provider>;
}

/** Access the back-office club filter. Throws if used outside <ClubFilterProvider>. */
export function useClubFilter(): ClubFilterValue {
  const ctx = useContext(ClubFilterContext);
  if (!ctx) throw new Error("useClubFilter must be used within <ClubFilterProvider>");
  return ctx;
}
